// client/src/components/campaign/CampaignForm.jsx
//
// Modal create/edit form for a marketing campaign (On-Screen 2.0).

import React, { useState } from 'react';
import { createCampaign, updateCampaign, CAMPAIGN_OBJECTIVES } from '../../services/campaignsApi';

const GOAL_FIELDS = [
  ['registrations', 'Registrations'],
  ['attendance', 'Attendance'],
  ['revenue', 'Revenue ($)'],
  ['reach', 'Social reach'],
];

export default function CampaignForm({ tenantId, campaign, onClose, onSaved }) {
  const editing = Boolean(campaign && campaign.id);
  const [form, setForm] = useState(() => ({
    name: campaign?.name || '',
    description: campaign?.description || '',
    objective: campaign?.objective || 'registration',
    startDate: campaign?.startDate ? String(campaign.startDate).slice(0, 10) : '',
    endDate: campaign?.endDate ? String(campaign.endDate).slice(0, 10) : '',
    pillars: (campaign?.contentPillars || []).join(', '),
    goal: { ...(campaign?.goal || {}) },
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setGoal = (k, v) =>
    setForm((f) => ({ ...f, goal: { ...f.goal, [k]: v === '' ? undefined : Number(v) } }));

  const save = async () => {
    if (!form.name.trim()) { setError('Give the campaign a name'); return; }
    setSaving(true);
    setError('');
    const payload = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      objective: form.objective,
      startDate: form.startDate || null,
      endDate: form.endDate || null,
      contentPillars: form.pillars.split(',').map((s) => s.trim()).filter(Boolean),
      goal: Object.fromEntries(Object.entries(form.goal).filter(([, v]) => Number.isFinite(v))),
    };
    try {
      const res = editing
        ? await updateCampaign(tenantId, campaign.id, payload)
        : await createCampaign(tenantId, payload);
      onSaved?.(res.data);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div
        className="relative w-full max-w-lg space-y-3 overflow-y-auto rounded-xl bg-white p-5 text-black dark:bg-neutral-900 dark:text-white"
        style={{ maxHeight: '90vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="font-semibold">{editing ? 'Edit campaign' : 'New campaign'}</h3>
        {error && <div className="text-sm text-red-600">{error}</div>}

        <input
          placeholder="Campaign name"
          value={form.name}
          onChange={(e) => set('name', e.target.value)}
          className="w-full rounded border border-black/15 bg-transparent px-2 py-1.5 text-sm dark:border-white/15"
        />
        <textarea
          placeholder="What is this campaign for?"
          rows={2}
          value={form.description}
          onChange={(e) => set('description', e.target.value)}
          className="w-full rounded border border-black/15 bg-transparent px-2 py-1.5 text-sm dark:border-white/15"
        />

        <label className="block text-xs opacity-70">Objective</label>
        <select
          value={form.objective}
          onChange={(e) => set('objective', e.target.value)}
          className="w-full rounded border border-black/15 bg-transparent px-2 py-1.5 text-sm capitalize dark:border-white/15"
        >
          {CAMPAIGN_OBJECTIVES.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>

        <div className="flex gap-2">
          <label className="flex-1 text-xs opacity-70">
            Start
            <input type="date" value={form.startDate} onChange={(e) => set('startDate', e.target.value)}
              className="mt-0.5 w-full rounded border border-black/15 bg-transparent px-2 py-1.5 text-sm dark:border-white/15" />
          </label>
          <label className="flex-1 text-xs opacity-70">
            End
            <input type="date" value={form.endDate} onChange={(e) => set('endDate', e.target.value)}
              className="mt-0.5 w-full rounded border border-black/15 bg-transparent px-2 py-1.5 text-sm dark:border-white/15" />
          </label>
        </div>

        <input
          placeholder="Content pillars (comma separated)"
          value={form.pillars}
          onChange={(e) => set('pillars', e.target.value)}
          className="w-full rounded border border-black/15 bg-transparent px-2 py-1.5 text-sm dark:border-white/15"
        />

        <div className="rounded-lg bg-black/5 p-3 dark:bg-white/5">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide opacity-60">KPI goals</div>
          <div className="grid grid-cols-2 gap-2">
            {GOAL_FIELDS.map(([k, label]) => (
              <label key={k} className="text-xs opacity-70">
                {label}
                <input
                  type="number" min="0"
                  value={form.goal[k] ?? ''}
                  onChange={(e) => setGoal(k, e.target.value)}
                  className="mt-0.5 w-full rounded border border-black/15 bg-transparent px-2 py-1 text-sm dark:border-white/15"
                />
              </label>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="rounded border border-black/15 px-3 py-1.5 text-sm dark:border-white/15">Cancel</button>
          <button onClick={save} disabled={saving} className="rounded bg-cobalt px-3 py-1.5 text-sm text-white disabled:opacity-50">
            {saving ? 'Saving…' : 'Save campaign'}
          </button>
        </div>
      </div>
    </div>
  );
}
